import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { evaluationsAPI, gradesAPI, courseAPI, groupAPI, workspaceAPI, remarksAPI } from '@/lib/api';
import { exportRecapToExcel, exportRecapToPDF } from '@/lib/recap-export';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Plus, 
  Loader2, 
  GraduationCap, 
  Edit2, 
  Trash2, 
  ClipboardList,
  Save,
  BookOpen,
  Award,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
  BarChart3,
  Trophy,
  TrendingUp,
  Users,
  Medal,
  FileSpreadsheet,
  FileText,
  MessageSquare,
  Pencil,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

// ─── Type badge config ───
const evaluationTypes = [
  { value: 'tp', label: 'TP', color: 'bg-blue-100 text-blue-700' },
  { value: 'exam', label: 'Examen', color: 'bg-purple-100 text-purple-700' },
  { value: 'continuous', label: 'Contrôle continu', color: 'bg-green-100 text-green-700' },
  { value: 'project', label: 'Projet', color: 'bg-amber-100 text-amber-700' },
  { value: 'quiz', label: 'Quiz', color: 'bg-rose-100 text-rose-700' },
  { value: 'assignment', label: 'Devoir', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'lab', label: 'Lab', color: 'bg-teal-100 text-teal-700' },
  { value: 'other', label: 'Autre', color: 'bg-slate-100 text-slate-700' },
];

const getTypeBadge = (type) => {
  const t = evaluationTypes.find(x => x.value === type);
  return <Badge className={t?.color || 'bg-slate-100 text-slate-700'}>{t?.label || type}</Badge>;
};

const getScoreColor = (score) => {
  if (score >= 16) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (score >= 14) return 'text-blue-700 bg-blue-50 border-blue-200';
  if (score >= 10) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-red-700 bg-red-50 border-red-200';
};

const getScoreBg = (score) => {
  if (score >= 16) return '#10b981';
  if (score >= 14) return '#3b82f6';
  if (score >= 10) return '#f59e0b';
  return '#ef4444';
};

// ═══════════════════════ REMARKS TAB ═══════════════════════
const RemarksTab = ({ workspaceId }) => {
  const { user } = useAuth();
  const [remarks, setRemarks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRemark, setEditingRemark] = useState(null);
  const [filterCourse, setFilterCourse] = useState('all');
  const [filterStudent, setFilterStudent] = useState('');
  const [formData, setFormData] = useState({
    student_id: '',
    course_id: '',
    remark: '',
    color: '#1e293b',
  });

  const remarkColors = [
    { value: '#1e293b', label: 'Noir' },
    { value: '#dc2626', label: 'Rouge' },
    { value: '#ea580c', label: 'Orange' },
    { value: '#16a34a', label: 'Vert' },
    { value: '#2563eb', label: 'Bleu' },
    { value: '#7c3aed', label: 'Violet' },
    { value: '#db2777', label: 'Rose' },
    { value: '#0891b2', label: 'Cyan' },
  ];

  useEffect(() => {
    fetchData();
  }, [workspaceId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [remarksRes, coursesRes, studentsRes] = await Promise.all([
        remarksAPI.list(workspaceId),
        courseAPI.list(workspaceId),
        workspaceAPI.listParticipants(workspaceId),
      ]);
      setRemarks(remarksRes.data);
      setCourses(coursesRes.data);
      setStudents(studentsRes.data.filter(s => s.role === 'candidate'));
    } catch (e) {
      console.error('Error fetching remarks:', e);
      toast.error('Erreur lors du chargement des remarques');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (remark = null) => {
    if (remark) {
      setEditingRemark(remark);
      setFormData({
        student_id: remark.student_id,
        course_id: remark.course_id,
        remark: remark.remark,
        color: remark.color || '#1e293b',
      });
    } else {
      setEditingRemark(null);
      setFormData({ student_id: '', course_id: '', remark: '', color: '#1e293b' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.remark.trim()) { toast.error('Veuillez saisir une remarque'); return; }
    setSaving(true);
    try {
      if (editingRemark) {
        await remarksAPI.update(editingRemark.id, { remark: formData.remark, color: formData.color });
        toast.success('Remarque modifiée');
      } else {
        if (!formData.student_id || !formData.course_id) {
          toast.error('Veuillez sélectionner un candidat et un cours');
          setSaving(false);
          return;
        }
        const student = students.find(s => s.id === formData.student_id);
        const course = courses.find(c => (c.id || c.course_id) === formData.course_id);
        await remarksAPI.create({
          workspace_id: workspaceId,
          student_id: formData.student_id,
          student_name: student ? `${student.first_name} ${student.last_name}` : 'Inconnu',
          student_email: student?.email || '',
          course_id: formData.course_id,
          course_title: course?.title || 'Inconnu',
          remark: formData.remark,
          color: formData.color,
        });
        toast.success('Remarque ajoutée');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette remarque ?')) return;
    try {
      await remarksAPI.delete(id);
      toast.success('Remarque supprimée');
      setRemarks(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredRemarks = useMemo(() => {
    let result = [...remarks];
    if (filterCourse !== 'all') result = result.filter(r => r.course_id === filterCourse);
    if (filterStudent) {
      const q = filterStudent.toLowerCase();
      result = result.filter(r =>
        r.student_name?.toLowerCase().includes(q) || r.student_email?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [remarks, filterCourse, filterStudent]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#7C3AED' }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[200px]">
          <Label className="text-xs mb-1 block">Rechercher un candidat</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Nom ou email..." value={filterStudent} onChange={e => setFilterStudent(e.target.value)} className="pl-8" />
          </div>
        </div>
        <div className="min-w-[200px]">
          <Label className="text-xs mb-1 block">Filtrer par cours</Label>
          <Select value={filterCourse} onValueChange={setFilterCourse}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les cours</SelectItem>
              {courses.map(c => <SelectItem key={c.id || c.course_id} value={c.id || c.course_id}>{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button onClick={() => openDialog()} className="gap-2">
          <Plus className="h-4 w-4" /> Nouvelle remarque
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="min-w-[180px]">Candidat</TableHead>
                  <TableHead className="min-w-[150px]">Cours</TableHead>
                  <TableHead className="min-w-[150px]">Formateur</TableHead>
                  <TableHead className="min-w-[300px]">Remarque</TableHead>
                  <TableHead className="min-w-[120px]">Date</TableHead>
                  <TableHead className="w-[80px] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRemarks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">Aucune remarque trouvée</p>
                      <p className="text-sm text-slate-400 mt-1">Ajoutez des remarques pour les candidats</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRemarks.map(r => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs bg-primary/10 text-primary font-bold">
                              {r.student_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm">{r.student_name}</p>
                            <p className="text-[11px] text-slate-400">{r.student_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{r.course_title}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{r.trainer_name}</span>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium whitespace-pre-wrap" style={{ color: r.color || '#1e293b' }}>{r.remark}</p>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-slate-500">
                          {r.updated_at ? format(parseISO(r.updated_at), 'd MMM yyyy, HH:mm', { locale: fr }) : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(r)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(r.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRemark ? 'Modifier la remarque' : 'Nouvelle remarque'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingRemark && (
              <>
                <div className="space-y-2">
                  <Label>Candidat *</Label>
                  <Select value={formData.student_id} onValueChange={(v) => setFormData({...formData, student_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner un candidat" /></SelectTrigger>
                    <SelectContent>
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cours *</Label>
                  <Select value={formData.course_id} onValueChange={(v) => setFormData({...formData, course_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner un cours" /></SelectTrigger>
                    <SelectContent>
                      {courses.map(c => <SelectItem key={c.id || c.course_id} value={c.id || c.course_id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {remarkColors.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    title={c.label}
                    onClick={() => setFormData({...formData, color: c.value})}
                    className="w-7 h-7 rounded-full border-2 transition-all hover:scale-110"
                    style={{
                      backgroundColor: c.value,
                      borderColor: formData.color === c.value ? '#7c3aed' : 'transparent',
                      boxShadow: formData.color === c.value ? '0 0 0 2px #c4b5fd' : 'none',
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Remarque *</Label>
              <Textarea
                value={formData.remark}
                onChange={(e) => setFormData({...formData, remark: e.target.value})}
                placeholder="Saisir votre remarque..."
                rows={4}
                style={{ color: formData.color }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingRemark ? 'Modifier' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═══════════════════════ RECAP TAB ═══════════════════════
const RecapTab = ({ workspaceId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');

  useEffect(() => {
    fetchSummary();
    fetchWorkspaceName();
  }, [workspaceId]);

  const fetchWorkspaceName = async () => {
    try {
      const res = await workspaceAPI.get(workspaceId);
      setWorkspaceName(res.data?.name || workspaceId);
    } catch (e) {
      setWorkspaceName(workspaceId);
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await evaluationsAPI.getAllGradesSummary(workspaceId);
      setSummary(res.data);
    } catch (e) {
      console.error('Error fetching summary:', e);
      toast.error('Erreur lors du chargement du récapitulatif');
    } finally {
      setLoading(false);
    }
  };

  const courseTitles = useMemo(() => {
    if (!summary) return [];
    const s = new Set();
    summary.assessments.forEach(a => { if (a.course_title) s.add(a.course_title); });
    return [...s].sort();
  }, [summary]);

  const filteredAssessments = useMemo(() => {
    if (!summary) return [];
    if (filterCourse === 'all') return summary.assessments;
    return summary.assessments.filter(a => a.course_title === filterCourse);
  }, [summary, filterCourse]);

  const filteredStudents = useMemo(() => {
    if (!summary) return [];
    let result = summary.students;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q));
    }
    return result;
  }, [summary, searchTerm]);

  // Recalculate averages when course filter applied
  const studentsWithFilteredAvg = useMemo(() => {
    if (!filteredStudents.length || !filteredAssessments.length) return filteredStudents;
    if (filterCourse === 'all') return filteredStudents;

    const filteredIds = new Set(filteredAssessments.map(a => a.id));
    return filteredStudents.map(s => {
      const scores = [];
      for (const aid of filteredIds) {
        const g = s.grades[aid];
        if (g) scores.push(g.normalised);
      }
      const avg = scores.length > 0 ? +(scores.reduce((a,b)=>a+b,0) / scores.length).toFixed(2) : null;
      return { ...s, filteredAverage: avg };
    }).sort((a, b) => {
      if (a.filteredAverage === null && b.filteredAverage === null) return 0;
      if (a.filteredAverage === null) return 1;
      if (b.filteredAverage === null) return -1;
      return b.filteredAverage - a.filteredAverage;
    });
  }, [filteredStudents, filteredAssessments, filterCourse]);

  // Stats
  const stats = useMemo(() => {
    if (!summary) return { avg: 0, best: 0, students: 0, assessments: 0 };
    const avgs = summary.students.filter(s => s.average !== null).map(s => s.average);
    return {
      avg: avgs.length ? +(avgs.reduce((a,b)=>a+b,0) / avgs.length).toFixed(2) : 0,
      best: avgs.length ? Math.max(...avgs) : 0,
      students: summary.total_students,
      assessments: summary.total_assessments,
    };
  }, [summary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#7C3AED" }} />
      </div>
    );
  }

  if (!summary || summary.total_assessments === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <GraduationCap className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Aucune épreuve trouvée dans ce workspace</p>
          <p className="text-sm text-slate-400 mt-1">Créez des quiz, devoirs, examens ou évaluations pour voir le récapitulatif</p>
        </CardContent>
      </Card>
    );
  }

  const displayStudents = studentsWithFilteredAvg;

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20"><TrendingUp className="h-5 w-5 text-white" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.avg}/20</p>
                <p className="text-xs text-white/80">Moyenne générale</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20"><Trophy className="h-5 w-5 text-white" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.best}/20</p>
                <p className="text-xs text-white/80">Meilleure moyenne</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20"><Users className="h-5 w-5 text-white" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.students}</p>
                <p className="text-xs text-white/80">Candidats</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20"><ClipboardList className="h-5 w-5 text-white" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.assessments}</p>
                <p className="text-xs text-white/80">Épreuves</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[200px]">
              <Label className="text-xs mb-1 block">Rechercher un candidat</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Nom ou email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8" />
              </div>
            </div>
            <div className="min-w-[200px]">
              <Label className="text-xs mb-1 block">Filtrer par cours</Label>
              <Select value={filterCourse} onValueChange={setFilterCourse}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les cours</SelectItem>
                  {courseTitles.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(searchTerm || filterCourse !== 'all') && (
              <Button variant="outline" size="sm" onClick={() => { setSearchTerm(''); setFilterCourse('all'); }}>
                <Filter className="mr-2 h-4 w-4" /> Réinitialiser
              </Button>
            )}
            <div className="flex-1" />
            {displayStudents.length > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  onClick={async () => {
                    try {
                      await exportRecapToExcel(displayStudents, filteredAssessments, workspaceName, filterCourse);
                      toast.success('Export Excel téléchargé');
                    } catch (err) { console.error(err); toast.error('Erreur export Excel'); }
                  }}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                </Button>
                <Button variant="outline" size="sm" className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  onClick={() => {
                    try {
                      exportRecapToPDF(displayStudents, filteredAssessments, workspaceName, filterCourse);
                      toast.success('Export PDF téléchargé');
                    } catch (err) { console.error(err); toast.error('Erreur export PDF'); }
                  }}>
                  <FileText className="h-4 w-4 mr-1" /> PDF
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Medal className="h-5 w-5 text-amber-500" />
            Classement des candidats
          </CardTitle>
          <CardDescription>
            {displayStudents.length} candidat(s) · {filteredAssessments.length} épreuve(s) · Scores normalisés sur /20
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="w-12 text-center sticky left-0 bg-slate-50 z-20">#</TableHead>
                  <TableHead className="min-w-[180px] sticky left-12 bg-slate-50 z-20">Candidat</TableHead>
                  {filteredAssessments.map(a => (
                    <TableHead key={a.id} className="text-center min-w-[90px] px-2">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-normal text-slate-400 truncate max-w-[80px]" title={a.course_title}>{a.course_title}</span>
                        <span className="text-xs font-medium truncate max-w-[80px]" title={a.title}>{a.title}</span>
                        {getTypeBadge(a.type)}
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="text-center min-w-[80px] sticky right-0 bg-slate-50 z-20">
                    <div className="flex flex-col items-center gap-0.5">
                      <Trophy className="h-4 w-4 text-amber-500" />
                      <span className="text-xs font-bold">Moyenne</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={filteredAssessments.length + 3} className="text-center py-12">
                      <GraduationCap className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">Aucun candidat trouvé</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayStudents.map((student, idx) => {
                    const avg = filterCourse !== 'all' ? student.filteredAverage : student.average;
                    const rank = filterCourse !== 'all' ? (avg !== null ? idx + 1 : null) : student.rank;
                    return (
                      <TableRow key={student.student_id} className="hover:bg-slate-50/50">
                        <TableCell className="text-center font-bold text-sm sticky left-0 bg-white z-10">
                          {rank !== null ? (
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                              rank === 1 ? 'bg-amber-100 text-amber-700' :
                              rank === 2 ? 'bg-slate-100 text-slate-600' :
                              rank === 3 ? 'bg-orange-100 text-orange-700' :
                              'bg-slate-50 text-slate-500'
                            }`}>
                              {rank}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="sticky left-12 bg-white z-10">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={student.picture} />
                              <AvatarFallback className="text-xs bg-primary/10 text-primary font-bold">
                                {student.name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{student.name}</p>
                              <p className="text-[11px] text-slate-400 truncate">{student.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        {filteredAssessments.map(a => {
                          const g = student.grades[a.id];
                          return (
                            <TableCell key={a.id} className="text-center px-2">
                              {g ? (
                                <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold border ${getScoreColor(g.normalised)}`}
                                  title={`${g.score}/${g.max_score}`}>
                                  {g.normalised.toFixed(1)}
                                </span>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center sticky right-0 bg-white z-10">
                          {avg !== null ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-bold text-white"
                              style={{ background: getScoreBg(avg) }}>
                              {avg.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════ MAIN PAGE ═══════════════════════
const EvaluationsPage = () => {
  const { user, isAdmin, isManager, isTrainer } = useAuth();
  const { workspaceId } = useParams();
  
  const [evaluations, setEvaluations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [allGrades, setAllGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('recap');
  
  // Filters for evaluations
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  
  // Filters for grades view
  const [gradeFilterCourse, setGradeFilterCourse] = useState('all');
  const [gradeFilterGroup, setGradeFilterGroup] = useState('all');
  const [gradeFilterType, setGradeFilterType] = useState('all');
  const [gradeFilterStudent, setGradeFilterStudent] = useState('');
  
  // Sorting for grades
  const [sortField, setSortField] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [gradesDialogOpen, setGradesDialogOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    course_id: '',
    title: '',
    description: '',
    evaluation_type: 'exam',
    max_score: 20,
    weight: 1,
    date: ''
  });
  
  // Grades data
  const [grades, setGrades] = useState({});
  const [existingGrades, setExistingGrades] = useState([]);

  useEffect(() => {
    if (activeTab === 'evaluations' || activeTab === 'grades') {
      fetchData();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'grades' && allGrades.length === 0 && evaluations.length > 0) {
      fetchAllGrades();
    }
  }, [activeTab, evaluations]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [evalRes, coursesRes, groupsRes] = await Promise.all([
        evaluationsAPI.getAll({ workspace_id: workspaceId }),
        courseAPI.list(workspaceId),
        groupAPI.list()
      ]);
      setEvaluations(evalRes.data);
      setCourses(coursesRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllGrades = async () => {
    try {
      setLoadingGrades(true);
      const gradesData = [];
      
      for (const eval_ of evaluations) {
        try {
          const gradesRes = await evaluationsAPI.getGrades(eval_.evaluation_id);
          for (const grade of gradesRes.data) {
            const course = courses.find(c => c.course_id === eval_.course_id);
            gradesData.push({
              ...grade,
              evaluation: eval_,
              course: course,
              normalized_score: ((grade.score / (eval_.max_score || 20)) * 20)
            });
          }
        } catch (e) {
          console.error('Error fetching grades:', e);
        }
      }
      
      setAllGrades(gradesData);
    } catch (error) {
      console.error('Error fetching all grades:', error);
    } finally {
      setLoadingGrades(false);
    }
  };

  const openCreateDialog = (evaluation = null) => {
    if (evaluation) {
      setFormData({
        course_id: evaluation.course_id,
        title: evaluation.title,
        description: evaluation.description || '',
        evaluation_type: evaluation.evaluation_type,
        max_score: evaluation.max_score,
        weight: evaluation.weight,
        date: evaluation.date ? format(parseISO(evaluation.date), 'yyyy-MM-dd') : ''
      });
      setSelectedEvaluation(evaluation);
      setEditMode(true);
    } else {
      setFormData({
        course_id: courses[0]?.course_id || '',
        title: '',
        description: '',
        evaluation_type: 'exam',
        max_score: 20,
        weight: 1,
        date: ''
      });
      setSelectedEvaluation(null);
      setEditMode(false);
    }
    setCreateDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        max_score: parseFloat(formData.max_score),
        weight: parseFloat(formData.weight),
        date: formData.date ? new Date(formData.date).toISOString() : null
      };
      
      if (editMode && selectedEvaluation) {
        await evaluationsAPI.update(selectedEvaluation.evaluation_id, data);
        toast.success('Évaluation mise à jour');
      } else {
        await evaluationsAPI.create(data);
        toast.success('Évaluation créée');
      }
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (evaluationId) => {
    if (!window.confirm('Supprimer cette évaluation et toutes ses notes ?')) return;
    
    try {
      await evaluationsAPI.delete(evaluationId);
      toast.success('Évaluation supprimée');
      fetchData();
      setAllGrades([]);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openGradesDialog = async (evaluation) => {
    setSelectedEvaluation(evaluation);
    setGrades({});
    
    try {
      const course = courses.find(c => c.course_id === evaluation.course_id);
      if (course?.student_ids?.length > 0) {
        const usersRes = await workspaceAPI.listParticipants(workspaceId);
        const courseStudents = usersRes.data.filter(s => 
          course.student_ids.includes(s.id) && s.role === 'student'
        );
        setStudents(courseStudents);
      } else {
        setStudents([]);
      }
      
      const gradesRes = await evaluationsAPI.getGrades(evaluation.evaluation_id);
      setExistingGrades(gradesRes.data);
      
      const gradesMap = {};
      gradesRes.data.forEach(g => {
        gradesMap[g.student_id] = { score: g.score, comment: g.comment || '' };
      });
      setGrades(gradesMap);
      
      setGradesDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des notes');
    }
  };

  const handleGradeChange = (studentId, field, value) => {
    setGrades(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], [field]: value }
    }));
  };

  const saveGrades = async () => {
    if (!selectedEvaluation) return;
    setSaving(true);
    try {
      const gradesArray = Object.entries(grades)
        .filter(([_, data]) => data.score !== undefined && data.score !== '')
        .map(([studentId, data]) => ({
          student_id: studentId,
          score: parseFloat(data.score),
          comment: data.comment || ''
        }));
      
      if (gradesArray.length === 0) {
        toast.error('Aucune note à sauvegarder');
        return;
      }
      
      await gradesAPI.bulkCreate(selectedEvaluation.evaluation_id, gradesArray);
      toast.success('Notes enregistrées');
      setGradesDialogOpen(false);
      setAllGrades([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const filteredEvaluations = evaluations.filter(e => {
    if (selectedCourse !== 'all' && e.course_id !== selectedCourse) return false;
    if (selectedType !== 'all' && e.evaluation_type !== selectedType) return false;
    return true;
  });

  // Sorting and filtering for grades view
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'score' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary" />
      : <ArrowDown className="h-4 w-4 text-primary" />;
  };

  const getStudentGroups = (studentId) => {
    return groups.filter(g => g.user_ids?.includes(studentId));
  };

  const filteredAndSortedGrades = useMemo(() => {
    let result = [...allGrades];
    if (gradeFilterCourse !== 'all') result = result.filter(g => g.course?.course_id === gradeFilterCourse);
    if (gradeFilterGroup !== 'all') result = result.filter(g => getStudentGroups(g.student_id).some(grp => grp.id === gradeFilterGroup));
    if (gradeFilterType !== 'all') result = result.filter(g => g.evaluation?.evaluation_type === gradeFilterType);
    if (gradeFilterStudent) {
      const s = gradeFilterStudent.toLowerCase();
      result = result.filter(g => g.student?.name?.toLowerCase().includes(s) || g.student?.email?.toLowerCase().includes(s));
    }
    result.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'student_name': valA = a.student?.name || ''; valB = b.student?.name || ''; break;
        case 'course': valA = a.course?.title || ''; valB = b.course?.title || ''; break;
        case 'evaluation': valA = a.evaluation?.title || ''; valB = b.evaluation?.title || ''; break;
        case 'type': valA = a.evaluation?.evaluation_type || ''; valB = b.evaluation?.evaluation_type || ''; break;
        case 'score': valA = a.normalized_score || 0; valB = b.normalized_score || 0; break;
        case 'date': valA = a.evaluation?.date || ''; valB = b.evaluation?.date || ''; break;
        default: valA = ''; valB = '';
      }
      if (typeof valA === 'number') return sortDirection === 'asc' ? valA - valB : valB - valA;
      return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
    return result;
  }, [allGrades, gradeFilterCourse, gradeFilterGroup, gradeFilterType, gradeFilterStudent, sortField, sortDirection, groups]);

  const resetGradeFilters = () => {
    setGradeFilterCourse('all'); setGradeFilterGroup('all');
    setGradeFilterType('all'); setGradeFilterStudent('');
  };

  const canManage = isAdmin || isManager || isTrainer;

  return (
    <WorkspaceLayout>
    <div className="space-y-6" data-testid="evaluations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold gradient-brand-text">Évaluations & Notes</h1>
          <p className="text-slate-500">Récapitulatif automatique de toutes les épreuves</p>
        </div>
        {canManage && (
          <Button onClick={() => { if (activeTab !== 'evaluations') setActiveTab('evaluations'); setTimeout(() => openCreateDialog(), 100); }} data-testid="create-evaluation-btn">
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle évaluation
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-[700px]">
          <TabsTrigger value="recap" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Récapitulatif
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Évaluations
          </TabsTrigger>
          <TabsTrigger value="grades" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Toutes les notes
          </TabsTrigger>
          <TabsTrigger value="remarks" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Remarques
          </TabsTrigger>
        </TabsList>

        {/* ══════ RECAP TAB ══════ */}
        <TabsContent value="recap" className="space-y-4">
          <RecapTab workspaceId={workspaceId} />
        </TabsContent>

        {/* ══════ REMARKS TAB ══════ */}
        <TabsContent value="remarks" className="space-y-4">
          <RemarksTab workspaceId={workspaceId} />
        </TabsContent>

        {/* ══════ EVALUATIONS TAB ══════ */}
        <TabsContent value="evaluations" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="w-48">
                  <Label className="mb-2 block">Cours</Label>
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger data-testid="course-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les cours</SelectItem>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <Label className="mb-2 block">Type</Label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger data-testid="type-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {evaluationTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Evaluations List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#7C3AED" }} />
            </div>
          ) : filteredEvaluations.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <GraduationCap className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Aucune évaluation trouvée</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table className="modern-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Évaluation</TableHead>
                      <TableHead>Cours</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Max</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEvaluations.map((evaluation) => {
                      const course = evaluation.course || courses.find(c => c.course_id === evaluation.course_id);
                      return (
                        <TableRow key={evaluation.evaluation_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{evaluation.title}</p>
                              {evaluation.description && (
                                <p className="text-sm text-slate-500 truncate max-w-xs">{evaluation.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <BookOpen className="h-4 w-4 text-slate-400" />
                              {course?.title || 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell>{getTypeBadge(evaluation.evaluation_type)}</TableCell>
                          <TableCell>{evaluation.max_score}</TableCell>
                          <TableCell>
                            {evaluation.date ? format(parseISO(evaluation.date), 'd MMM yyyy', { locale: fr }) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {canManage && (
                                <>
                                  <Button variant="outline" size="sm" onClick={() => openGradesDialog(evaluation)}>
                                    <ClipboardList className="mr-1 h-4 w-4" /> Notes
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => openCreateDialog(evaluation)}>
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(evaluation.evaluation_id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ══════ ALL GRADES TAB ══════ */}
        <TabsContent value="grades" className="space-y-4">
          {/* Filters for grades */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
                <div>
                  <Label className="text-xs mb-1 block">Rechercher étudiant</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Nom ou email..." value={gradeFilterStudent} onChange={(e) => setGradeFilterStudent(e.target.value)} className="pl-8" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Cours</Label>
                  <Select value={gradeFilterCourse} onValueChange={setGradeFilterCourse}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les cours</SelectItem>
                      {courses.map(c => <SelectItem key={c.course_id} value={c.course_id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Groupe</Label>
                  <Select value={gradeFilterGroup} onValueChange={setGradeFilterGroup}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les groupes</SelectItem>
                      {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Type d'évaluation</Label>
                  <Select value={gradeFilterType} onValueChange={setGradeFilterType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {evaluationTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" size="sm" onClick={resetGradeFilters}>
                    <Filter className="mr-2 h-4 w-4" /> Réinitialiser
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Grades Table */}
          {loadingGrades ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#7C3AED" }} />
            </div>
          ) : (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{filteredAndSortedGrades.length} note(s) trouvée(s)</CardTitle>
                  <p className="text-sm text-slate-500">Cliquez sur les en-têtes pour trier</p>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <Table className="modern-table">
                      <TableHeader className="sticky top-0 bg-slate-50 z-10">
                        <TableRow>
                          <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('student_name')}>
                            <div className="flex items-center gap-2">Étudiant {getSortIcon('student_name')}</div>
                          </TableHead>
                          <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('course')}>
                            <div className="flex items-center gap-2">Cours {getSortIcon('course')}</div>
                          </TableHead>
                          <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('evaluation')}>
                            <div className="flex items-center gap-2">Évaluation {getSortIcon('evaluation')}</div>
                          </TableHead>
                          <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('type')}>
                            <div className="flex items-center gap-2">Type {getSortIcon('type')}</div>
                          </TableHead>
                          <TableHead className="cursor-pointer hover:bg-slate-100 text-right" onClick={() => handleSort('score')}>
                            <div className="flex items-center justify-end gap-2">Note /20 {getSortIcon('score')}</div>
                          </TableHead>
                          <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('date')}>
                            <div className="flex items-center gap-2">Date {getSortIcon('date')}</div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAndSortedGrades.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-12">
                              <GraduationCap className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                              <p className="text-slate-500">Aucune note trouvée</p>
                            </TableCell>
                          </TableRow>
                        ) : filteredAndSortedGrades.map((grade, idx) => (
                          <TableRow key={`${grade.grade_id}-${idx}`}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={grade.student?.picture} />
                                  <AvatarFallback className="text-xs bg-primary/10 text-primary">{grade.student?.name?.charAt(0) || '?'}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="font-medium text-sm truncate">{grade.student?.name || 'N/A'}</p>
                                  <p className="text-xs text-slate-500 truncate">{grade.student?.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell><span className="text-sm">{grade.course?.title || 'N/A'}</span></TableCell>
                            <TableCell>
                              <div>
                                <span className="text-sm">{grade.evaluation?.title || 'N/A'}</span>
                                {grade.comment && <p className="text-xs text-slate-500 truncate max-w-[150px]" title={grade.comment}>{grade.comment}</p>}
                              </div>
                            </TableCell>
                            <TableCell>{getTypeBadge(grade.evaluation?.evaluation_type)}</TableCell>
                            <TableCell className="text-right">
                              <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(grade.normalized_score)}`}>
                                {grade.normalized_score.toFixed(1)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-600">
                                {grade.evaluation?.date ? format(parseISO(grade.evaluation.date), 'd MMM yyyy', { locale: fr }) : '-'}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Evaluation Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier l\'évaluation' : 'Nouvelle évaluation'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="course">Cours *</Label>
              <Select value={formData.course_id} onValueChange={(v) => setFormData({...formData, course_id: v})}>
                <SelectTrigger><SelectValue placeholder="Sélectionner un cours" /></SelectTrigger>
                <SelectContent>
                  {courses.map(course => <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Titre *</Label>
              <Input id="title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Ex: Examen final" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Description de l'évaluation..." rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.evaluation_type} onValueChange={(v) => setFormData({...formData, evaluation_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {evaluationTypes.map(type => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input id="date" type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max_score">Note max *</Label>
                <Input id="max_score" type="number" min="1" max="100" value={formData.max_score} onChange={(e) => setFormData({...formData, max_score: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight">Coefficient</Label>
                <Input id="weight" type="number" min="0.1" step="0.1" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editMode ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Grades Entry Dialog */}
      <Dialog open={gradesDialogOpen} onOpenChange={setGradesDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Saisie des notes - {selectedEvaluation?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedEvaluation && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedEvaluation.title}</p>
                    <p className="text-sm text-slate-500">Note maximale: {selectedEvaluation.max_score} | Coefficient: {selectedEvaluation.weight}</p>
                  </div>
                  {getTypeBadge(selectedEvaluation.evaluation_type)}
                </div>
              </div>
              {students.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Aucun étudiant inscrit à ce cours</div>
              ) : (
                <Table className="modern-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Étudiant</TableHead>
                      <TableHead className="w-24">Note /{selectedEvaluation.max_score}</TableHead>
                      <TableHead>Commentaire</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow key={student.user_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={student.picture} />
                              <AvatarFallback className="bg-primary/10 text-primary text-sm">{student.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{student.name}</p>
                              <p className="text-xs text-slate-500">{student.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input type="number" min="0" max={selectedEvaluation.max_score} step="0.5"
                            value={grades[student.user_id]?.score ?? ''} onChange={(e) => handleGradeChange(student.user_id, 'score', e.target.value)}
                            className="w-20" placeholder="--" />
                        </TableCell>
                        <TableCell>
                          <Input value={grades[student.user_id]?.comment ?? ''} onChange={(e) => handleGradeChange(student.user_id, 'comment', e.target.value)}
                            placeholder="Commentaire optionnel" className="text-sm" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setGradesDialogOpen(false)}>Annuler</Button>
                <Button onClick={saveGrades} disabled={saving || students.length === 0}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" /> Enregistrer les notes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </WorkspaceLayout>
  );
};

export default EvaluationsPage;
